<template>
  <v-app>
    <Header ref="header"/>
    <Sidebar/>
    <ContentArea>
      <ButtonBar>
        <v-text-field v-model="filter" append-icon="search" :placeholder="$t('filter')" class="filterInput" v-if="!currentUser"/>
        <v-btn @click="impersonate" v-if="canImpersonate" class="bw-4" style="float: left;">{{ $t('pages.user.button.impersonate') }}</v-btn>
        <v-btn @click="createNewUser" class="bw-6" v-if="!currentUser">{{ $t('pages.user.button.create') }}</v-btn>
        <v-btn @click="saveUser" :disabled="saveButtonDisabled" class="bw-3" v-if="currentUser">{{ $t('button.save') }}</v-btn>
        <v-btn @click="confirmResetPasswordDialog = true" v-if="currentUser && currentUser.id" class="bw-5">{{ $t('pages.user.button.resetPassword') }}</v-btn>
        <v-btn v-if="canDelete" @click="confirmDeleteDialog = true" class="bw-3">{{ $t('button.delete') }}</v-btn>
        <v-btn @click="closeUser" class="bw-3" v-if="currentUser">{{ $t('button.close') }}</v-btn>
      </ButtonBar>
      <v-container fluid py-2 px-3 v-if="!currentUser" >
        <v-layout style="max-height: calc(100vh - 160px);">
          <v-flex text-xs-right style="border: 1px solid #DDDDDD; border-radius: 3px; overflow-y: auto;">
            <v-data-table :headers="tableHeaders" :items="users" :search="filter" hide-default-footer>
              <template v-slot:item="props">
                <tr @click="selectRow(props.item)" class="selectable">
                  <td class="text-xs-left" style="white-space: nowrap;">{{ props.item.userName }}</td>
                  <td class="text-xs-left" style="white-space: nowrap;">{{ props.item.fullName }}</td>
                  <td class="text-xs-left">{{ props.item.email }}</td>
                  <td class="text-xs-left" v-if="isSuperAdmin">{{ props.item.superAdmin ? 'Yes' : 'No' }}</td>
                  <td class="text-xs-left" v-if="isSuperAdmin"><div v-if="props.item.organization">{{ props.item.organization.name }}</div></td>
                  <td class="text-xs-left">{{ userRolesToString(props.item.roles) }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </v-container>
      <Panel :title="$t('pages.user.edit.header.' + (currentUser.id ? 'update' : 'create'))" v-if="currentUser">
        <PanelColumn min-height="180">
          <v-flex xs12 sm4 lg2 style="height: 35px;">
            <v-label>{{ $t('pages.user.edit.fields.userName') }}</v-label>
          </v-flex>
          <v-flex xs12 sm8 lg10 style="line-height: 26px; height: 35px;">
            <TextField v-model="currentUser.userName" max-width="300"/>
          </v-flex>

          <v-flex xs12 sm4 lg2 style="height: 35px;">
            <v-label>{{ $t('pages.user.edit.fields.fullName') }}</v-label>
          </v-flex>
          <v-flex xs12 sm8 lg10 style="line-height: 26px; height: 35px;">
            <TextField v-model="currentUser.fullName" max-width="500"/>
          </v-flex>

          <v-flex xs12 sm4 lg2 style="height: 35px;">
            <v-label>{{ $t('pages.user.edit.fields.email') }}</v-label>
          </v-flex>
          <v-flex xs12 sm8 lg10 style="line-height: 26px; height: 35px;">
            <TextField v-model="currentUser.email" max-width="500"/>
          </v-flex>

          <v-flex xs12 sm4 lg2 style="height: 35px;" v-if="isSuperAdmin">
            <v-label>{{ $t('pages.user.edit.fields.superAdmin') }}</v-label>
          </v-flex>
          <v-flex xs12 sm8 lg10 style="line-height: 26px; height: 35px;" v-if="isSuperAdmin">
            <v-simple-checkbox v-model="currentUser.superAdmin" color="primary"/>
          </v-flex>

          <v-flex xs12 sm4 lg2 style="height: 35px;" v-if="isSuperAdmin && !currentUser.superAdmin">
            <v-label>{{ $t('pages.user.edit.fields.organization') }}</v-label>
          </v-flex>
          <v-flex xs12 sm8 lg10 style="height: 35px;" v-if="isSuperAdmin && !currentUser.superAdmin">
            <v-select outlined :items="organizations" v-model="currentUser.organization" item-text="name" return-object/>
          </v-flex>

          <v-flex xs12 sm4 lg2 style="height: 35px;" v-if="!currentUser.superAdmin">
            <v-label>{{ $t('pages.user.edit.fields.roles') }}</v-label>
          </v-flex>
          <v-flex xs12 sm8 lg10 style="height: 35px;" v-if="!currentUser.superAdmin">
            <v-select
              v-model="currentUser.roles"
              :items="roles"
              :menu-props="{ maxHeight: '300' }"
              multiple
              item-value="role"
              item-text="label"
              outlined
              style="max-width: 300px; height: 100px;"
            />
          </v-flex>

          <div v-if="errorMessage !== ''" class="error-message-frame top-margin">{{ errorMessage }}</div>
        </PanelColumn>
      </Panel>
    </ContentArea>
    <Footer/>

    <v-dialog v-model="confirmResetPasswordDialog" persistent width="500">
      <v-card pa-5>
        <v-card-title class="headline">{{ $t('pages.user.dialog.resetPassword.header') }}</v-card-title>
        <v-card-text v-if="currentUser">
          {{ $t('pages.user.dialog.resetPassword.message', { userName: currentUser.userName }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn @click="this.resetPassword">{{ $t('button.yes') }}</v-btn>
          <v-btn @click="confirmResetPasswordDialog = false">{{ $t('button.no') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDeleteDialog" persistent width="350">
      <v-card pa-5>
        <v-card-title class="headline">{{ $t('pages.user.dialog.delete.header') }}</v-card-title>
        <v-card-text v-if="currentUser" style="padding-top: 20px;">
          {{ $t('pages.user.dialog.delete.message', { userName: currentUser.userName }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn @click="this.delete">{{ $t('button.yes') }}</v-btn>
          <v-btn @click="confirmDeleteDialog = false">{{ $t('button.no') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import Footer from '../components/Footer'
import ContentArea from '../components/ContentArea'
import ButtonBar from '../components/ButtonBar'
import Panel from '../components/Panel'
import PanelColumn from '../components/PanelColumn'
import TextField from '../components/TextField'
import axios from 'axios'

export default {
  name: 'Users',
  components: { Header, Sidebar, Footer, ContentArea, ButtonBar, Panel, PanelColumn, TextField },
  data () {
    return {
      filter: null,
      users: [],
      organizations: [],
      currentUser: null,
      confirmResetPasswordDialog: false,
      confirmDeleteDialog: false,
      roles: [],
      errorMessage: ''
    }
  },
  created: function () {
    this.$store.dispatch('page/setSidebarItem', { key: 'users' })
    this.initializeRoles()
    this.loadUsers()
    if (this.isSuperAdmin) {
      this.loadOrganizations()
    }
  },
  computed: {
    tableHeaders: function () {
      let headers = []
      headers.push({ text: this.$t('pages.user.table.columns.userName'), value: 'userName', sortable: true })
      headers.push({ text: this.$t('pages.user.table.columns.fullName'), value: 'fullName', sortable: true })
      headers.push({ text: this.$t('pages.user.table.columns.email'), value: 'email', sortable: true })
      if (this.isSuperAdmin) {
        headers.push({ text: this.$t('pages.user.table.columns.superAdmin'), value: 'superAdmin', sortable: true })
        headers.push({ text: this.$t('pages.user.table.columns.organization'), value: 'organization.name', sortable: true })
      }
      headers.push({ text: this.$t('pages.user.table.columns.roles'), value: 'roles', sortable: false })
      return headers
    },
    canDelete: function () {
      return this.currentUser && this.currentUser.id && this.currentUser.userName !== this.$store.state.session.userName
    },
    canImpersonate: function () {
      return this.currentUser && this.currentUser.id && !this.currentUser.superAdmin && (this.isSuperAdmin || !this.currentUser.roles.includes('ADMIN'))
    },
    isSuperAdmin: function () {
      return this.$store.getters['session/isSuperAdmin']
    },
    saveButtonDisabled: function () {
      return !this.currentUser || this.currentUser.userName.trim() === '' || this.currentUser.fullName.trim() === '' || this.currentUser.email.trim() === '' || (this.isSuperAdmin && !this.currentUser.superAdmin && !this.currentUser.organization)
    }
  },
  methods: {
    initializeRoles () {
      axios.get('/backend/ui/user/roles')
        .then(response => {
          let roles = response.data
          this.roles = []
          for (let r of roles) {
            let role = {
              role: r,
              label: this.$t('userRoles.' + r)
            }
            this.roles.push(role)
          }
        })
    },
    loadUsers () {
      let url = this.isSuperAdmin ? '/backend/ui/user/all' : '/backend/ui/user'
      axios.get(url)
        .then(response => {
          this.users = response.data
        })
    },
    loadOrganizations () {
      axios.get('/backend/ui/organization')
        .then(response => {
          this.organizations = response.data
        })
    },
    createNewUser () {
      this.errorMessage = ''
      this.currentUser = {
        id: null,
        userName: '',
        fullName: '',
        email: '',
        superAdmin: false,
        organization: null
      }
    },
    saveUser () {
      this.errorMessage = ''
      axios.post('/backend/ui/user', this.currentUser)
        .then(response => {
          this.loadUsers()
          this.currentUser = null
        })
        .catch(error => {
          if (error.response.data.code === 'IN_USE') {
            this.errorMessage = this.$t('pages.user.edit.error.inUse', { userName: this.currentUser.userName })
          } else {
            this.errorMessage = error.response.data.message
          }
        })
    },
    resetPassword () {
      this.confirmResetPasswordDialog = false
      axios.post('/backend/ui/user/' + this.currentUser.id + '/resetPassword')
        .then((response) => {
          this.$refs.header.showSnackbar(this.$t('pages.user.snackbar.resetPassword.succeed'))
        })
        .catch((error) => {
          console.log('Error: \'' + error.message + '\'')
          this.$refs.header.showSnackbar(this.$t('pages.user.snackbar.resetPassword.failed'))
        })
    },
    delete () {
      // console.log('Delete user: ' + this.currentUser.userName)
      axios.delete('/backend/ui/user/' + this.currentUser.id)
        .then(response => {
          this.loadUsers()
        })
        .catch(error => {
          console.log(error.response.data.message)
        })
      this.confirmDeleteDialog = false
      this.currentUser = null
    },
    impersonate () {
      this.$store.dispatch('session/impersonate', this.currentUser)
        .then((response) => {
          console.log('Impersonate response: ' + response)
          let menuItems = this.$store.state.sidebar.items.filter(i => (!i.roles || this.$store.getters['session/hasRole'](i.roles)) && !(i.excludeRoles && !this.$store.getters['session/hasNotRole'](i.excludeRoles)))
          if (menuItems && menuItems.length > 0 && menuItems[0].routerName) {
            this.$router.push({name: menuItems[0].routerName}).catch(() => {})
          } else {
            this.$router.push({ name: 'Dashboard' })
          }
        })
        .catch((error) => {
          console.log('Error: \'' + error.message + '\'')
        })
    },
    closeUser () {
      this.currentUser = null
      this.loadUsers()
    },
    selectRow (user) {
      this.initializeRoles()
      this.errorMessage = ''
      this.currentUser = user
    },
    userRolesToString (roles) {
      if (roles && roles.length > 0) {
        let s = ''
        for (let role of roles) {
          if (s !== '') {
            s += ', '
          }
          s += this.$t('userRoles.' + role)
        }
        return s
      }
      return ''
    }
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/pages/Users.vue